import { useCallback, useEffect, useRef, useState } from 'react'
import Config from '@config'
import { useLanguage } from '@context'
import {
  fetchVenueSuggestions,
  newSearchSession,
  retrieveVenue,
  type VenuePlace,
  type VenueSuggestion,
} from '@utils'

const DEBOUNCE_MS = 250
const MIN_CHARS = 2

export type SuggestionsStatus = 'idle' | 'loading' | 'ready' | 'error'

/**
 * Debounced venue suggestions for what is being typed. One Mapbox session token
 * per typing session (suggest calls + the final retrieve), renewed after a pick.
 */
export function useVenueSuggestions(query: string, enabled: boolean) {
  const { currentLanguage } = useLanguage()
  const session = useRef(newSearchSession())
  const [status, setStatus] = useState<SuggestionsStatus>('idle')
  const [suggestions, setSuggestions] = useState<VenueSuggestion[]>([])

  useEffect(() => {
    const text = query.trim()
    if (!enabled || text.length < MIN_CHARS) {
      setStatus('idle')
      setSuggestions([])
      return undefined
    }
    setStatus('loading')
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      try {
        const found = await fetchVenueSuggestions(text, {
          token: Config.MAPBOX_ACCESS_TOKEN,
          language: currentLanguage,
          session: session.current,
          signal: controller.signal,
        })
        setSuggestions(found)
        setStatus('ready')
      } catch {
        if (controller.signal.aborted) return
        setSuggestions([])
        setStatus('error')
      }
    }, DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [query, enabled, currentLanguage])

  const pick = useCallback(
    async (suggestion: VenueSuggestion): Promise<VenuePlace | null> => {
      try {
        return await retrieveVenue(suggestion, {
          token: Config.MAPBOX_ACCESS_TOKEN,
          language: currentLanguage,
          session: session.current,
        })
      } catch {
        return null
      } finally {
        session.current = newSearchSession()
      }
    },
    [currentLanguage],
  )

  return { status, suggestions, pick }
}
